// ─────────────────────────────────────────────────────────────────────────────
// Adding money to the customer wallet.
//
// The server creates the Razorpay order (so the amount is the server's), Razorpay
// takes the payment, and the server confirms it with Razorpay before crediting.
// Resolves - never throws - to one of:
//   { status: "success", amount, balance }  paid and credited
//   { status: "failed", reason }            a payment attempt failed and the window closed
//   { status: "cancelled" }                 closed without paying
//   { status: "pending" }                   paid; the confirmation is not back yet - the
//                                           wallet screen finishes it on its own
//   { status: "unavailable", code }         payments are not switched on yet
//   { status: "error", code? }              could not start
// ─────────────────────────────────────────────────────────────────────────────

import { apiRequest } from "./api";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window.Razorpay !== "undefined") { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export async function startWalletTopUp({ amount, user, description = "Wallet" }) {
  let order;
  try {
    order = await apiRequest("/customer-wallet/topup/order", { method: "POST", body: JSON.stringify({ amount }) });
  } catch (err) {
    if (err?.status === 503) return { status: "unavailable", code: err.code };
    return { status: "error", code: err?.code };
  }
  if (!order?.orderId) return { status: "error" };

  const loaded = await loadRazorpay();
  if (!loaded || typeof window.Razorpay === "undefined") return { status: "error" };

  return new Promise((resolve) => {
    let settled = false;
    let lastFailure = null; // Razorpay lets the customer retry inside its window
    const finish = (result) => { if (!settled) { settled = true; resolve(result); } };
    const phone = String(user?.phone || "").replace(/\D/g, "").slice(-10);

    const checkout = new window.Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.orderId,
      name: "Sramika",
      description,
      image: "/logo192.png",
      prefill: { name: user?.name || "", contact: phone ? `+91${phone}` : "" },
      theme: { color: "#7BAE00" },
      modal: {
        ondismiss() {
          finish(lastFailure ? { status: "failed", reason: lastFailure } : { status: "cancelled" });
        },
      },
      handler: async (response) => {
        try {
          const done = await apiRequest("/customer-wallet/topup/verify", {
            method: "POST",
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          finish({ status: "success", amount: done.amount, balance: done.balance });
        } catch (err) {
          // A refusal is final; anything else means paid-but-not-yet-confirmed.
          if (err?.code === "BAD_SIGNATURE" || err?.code === "PAYMENT_NOT_VALID") finish({ status: "failed", reason: err.code });
          else finish({ status: "pending" });
        }
      },
    });
    checkout.on("payment.failed", (resp) => { lastFailure = resp?.error?.description || "failed"; });
    checkout.open();
  });
}
